import { useEffect, useState, useMemo } from "react";
import useLocalStorage from "../../Hooks/useLocalStorage";
import { BASE_URL } from "../../Config/api.config";

// Types based on your jobs API response
interface JobCategoryStats {
    category_id: number;
    category_name: string;
    job_count: number;
    percentage: number;
}

interface JobSkillStats {
    skill_id: number;
    skill_name: string;
    usage_count: number;
    percentage: number;
}

interface LocationStats {
    country_code: string;
    city: string;
    job_count: number;
    percentage: number;
}

interface SalaryStats {
    total_jobs: number;
    average_salary: number;
    min_salary: number;
    max_salary: number;
    currency_code: string;
    job_type: string;
}

interface ScheduleStats {
    dayname: string;
    job_count: number;
    avg_hours_per_day: number;
}

interface MonthlyTrend {
    year: number;
    month: number;
    job_count: number;
    unique_employers: number;
    avg_salary: number;
}

interface ApplicationStats {
    job_id: number;
    title: string;
    total_applications: number;
    hired_count: number;
    declined_count: number;
}

interface JobTypeStats {
    type_name: string;
    cant: number;
}

interface JobByStatus {
    status_name: string;
    cant: string;
}

interface JobDetail {
    job_id: number;
    title: string;
    client_id: number;
    employer_name: string;
    category_name: string;
    job_type: string;
    budget: number;
    currency_code: string;
    country_code: string;
    city: string;
    date_created: string;
    job_status: string;
    proposals: number;
    skills: string[];
    time_slots: string;
}

interface ApiResponse {
    status: string;
    message: string;
    result: {
        total_jobs: string;
        total_completed_jobs: string;
        total_expired_jobs: string;
        total_ondate_jobs: string;
        active_jobs: string;
        total_employers: string;
        total_applications: string;
        avg_salary: string;
        jobs_by_status: JobByStatus[];
        jobs_by_type: JobTypeStats[];
        top_categories: JobCategoryStats[];
        top_skills: JobSkillStats[];
        preferred_locations: LocationStats[];
        salary_stats: SalaryStats[];
        schedule_stats: ScheduleStats[];
        monthly_trends: MonthlyTrend[];
        application_stats: ApplicationStats[];
        recent_jobs: { status: string, result: JobDetail[] };
        filters: any;
        paginate: {
            rows: number;
            pages: number;
        };
    };
}

const JobStats = () => {
    const [adminData] = useLocalStorage("adminData", "");
    const [data, setData] = useState<ApiResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState<{
        key: string;
        direction: "ascending" | "descending";
    }>({
        key: "job_id",
        direction: "descending",
    });
    const [selectedJob, setSelectedJob] = useState<JobDetail | null>(null);
    const [activeTab, setActiveTab] = useState<"overview" | "jobs">("overview");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [filters, setFilters] = useState({
        job_type: "",
        job_status: "",
        category: "",
        date_from: "",
        date_to: ""
    });

    const fetchData = async (
        page: number = currentPage,
        limit: number = itemsPerPage
    ) => {
        try {
            setLoading(true);
            setError(null);
            const offset = (page - 1) * limit;

            // Build query parameters
            const params = new URLSearchParams({
                limit: limit.toString(),
                offset: offset.toString(),
                language_code: 'en'
            });

            // Add filters
            if (filters.job_type) params.append('job_type_id_list', filters.job_type);
            if (filters.job_status) params.append('job_status_id_list', filters.job_status);
            if (filters.category) params.append('job_categories_list', filters.category);
            if (filters.date_from) params.append('date_created_start', filters.date_from);
            if (filters.date_to) params.append('date_created_end', filters.date_to);

            const response = await fetch(
                `${BASE_URL}adm.jobs.stats?${params.toString()}`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${adminData.uid}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result: ApiResponse = await response.json();

            if (result.status === "ok") {
                setData(result);
            } else {
                throw new Error(result.message || "Failed to fetch data");
            }
        } catch (err) {
            console.error("Error fetching job data:", err);
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (adminData?.uid) {
            fetchData();
        }
    }, [adminData, filters]);

    // Handle page change
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        fetchData(newPage, itemsPerPage);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        fetchData(1, newItemsPerPage);
    };

    // Filter and sort jobs (client-side for current page only)
    const filteredAndSortedJobs = useMemo(() => {
        if (!data?.result?.recent_jobs?.result) return [];

        let filtered = data.result.recent_jobs.result.filter(
            (job) =>
                job.title?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
                job.employer_name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
                job.category_name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
                job.job_id.toString().includes(searchTerm)
        );

        // Sort jobs
        filtered.sort((a, b) => {
            let aValue: any = a[sortConfig.key as keyof JobDetail];
            let bValue: any = b[sortConfig.key as keyof JobDetail];

            // Handle numeric sorting
            if (sortConfig.key === "budget" || sortConfig.key === "proposals") {
                aValue = aValue || 0;
                bValue = bValue || 0;
            }

            // Handle date sorting
            if (sortConfig.key === "date_created") {
                aValue = new Date(aValue).getTime();
                bValue = new Date(bValue).getTime();
            }

            if (aValue < bValue) {
                return sortConfig.direction === "ascending" ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === "ascending" ? 1 : -1;
            }
            return 0;
        });

        return filtered;
    }, [data, searchTerm, sortConfig]);

    const handleSort = (key: string) => {
        setSortConfig((prevConfig) => ({
            key,
            direction:
                prevConfig.key === key && prevConfig.direction === "ascending"
                    ? "descending"
                    : "ascending",
        }));
    };

    const formatNumber = (num: number | string) => {
        const value = typeof num === "string" ? parseFloat(num) : num;
        return new Intl.NumberFormat().format(isNaN(value) ? 0 : value);
    };

    const formatCurrency = (amount: number | string, currency: string = 'USD') => {
        const value = typeof amount === "string" ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(isNaN(value) ? 0 : value);
    };

    const formatDate = (dateString: string) => {
        if (!dateString || dateString === "0000-00-00 00:00:00") return "Never";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const getJobStatusColor = (status: string) => {
        const statusColors: { [key: string]: string } = {
            'active': 'bg-green-100 text-green-800',
            'completed': 'bg-blue-100 text-blue-800',
            'expired': 'bg-red-100 text-red-800',
            'posted': 'bg-purple-100 text-purple-800',
            'draft': 'bg-gray-100 text-gray-800',
            'cancelled': 'bg-yellow-100 text-yellow-800'
        };
        return statusColors[status.toLowerCase()] || 'bg-gray-100 text-gray-800';
    };

    // const getJobTypeColor = (type: string) => {
    //     const typeColors: { [key: string]: string } = {
    //         'remote': 'bg-green-100 text-green-800',
    //         'inperson': 'bg-blue-100 text-blue-800',
    //         'hybrid': 'bg-purple-100 text-purple-800'
    //     };
    //     return typeColors[type.toLowerCase()] || 'bg-gray-100 text-gray-800';
    // };

    // Generate page numbers for pagination
    const getPageNumbers = () => {
        if (!data?.result?.paginate) return [];

        const { pages } = data.result.paginate;
        const pagesArray = [];
        const maxVisiblePages = 5;

        let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(pages, startPage + maxVisiblePages - 1);

        // Adjust start page if we're near the end
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(1, endPage - maxVisiblePages + 1);
        }

        for (let i = startPage; i <= endPage; i++) {
            pagesArray.push(i);
        }

        return pagesArray;
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading job statistics...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="text-red-500 text-6xl mb-4">⚠️</div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        Error Loading Data
                    </h3>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <button
                        onClick={() => fetchData()}
                        className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <p className="text-gray-600">No data available</p>
                </div>
            </div>
        );
    }

    const { result } = data;
    const pageNumbers = getPageNumbers();

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Job Statistics Dashboard
                    </h1>
                    <p className="text-gray-600">
                        Comprehensive overview of job postings, applications, and market trends
                    </p>
                </div>

                {/* Tabs */}
                <div className="mb-8">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            <button
                                onClick={() => setActiveTab("overview")}
                                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "overview"
                                    ? "border-blue-500 text-blue-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                    }`}
                            >
                                Market Overview
                            </button>
                            <button
                                onClick={() => setActiveTab("jobs")}
                                className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === "jobs"
                                    ? "border-blue-500 text-blue-600"
                                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                                    }`}
                            >
                                Jobs ({formatNumber(result.total_jobs)})
                            </button>
                        </nav>
                    </div>
                </div>

                {activeTab === "overview" && (
                    <div className="space-y-6">
                        {/* Summary Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">
                                            Total Jobs
                                        </p>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {formatNumber(result.total_jobs)}
                                        </p>
                                    </div>
                                    <div className="p-3 bg-blue-100 rounded-lg">
                                        <span className="text-blue-600 text-xl">💼</span>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <span className="text-sm text-green-600 font-medium">
                                        {formatNumber(result.active_jobs)} active
                                    </span>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">
                                            Total Applications
                                        </p>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {formatNumber(result.total_applications)}
                                        </p>
                                    </div>
                                    <div className="p-3 bg-green-100 rounded-lg">
                                        <span className="text-green-600 text-xl">📨</span>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <span className="text-sm text-gray-600">
                                        Across all jobs
                                    </span>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">
                                            Average Salary
                                        </p>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {formatCurrency(result.avg_salary)}
                                        </p>
                                    </div>
                                    <div className="p-3 bg-purple-100 rounded-lg">
                                        <span className="text-purple-600 text-xl">💰</span>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <span className="text-sm text-gray-600">
                                        Market average
                                    </span>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600">
                                            Active Employers
                                        </p>
                                        <p className="text-2xl font-bold text-gray-900">
                                            {formatNumber(result.total_employers)}
                                        </p>
                                    </div>
                                    <div className="p-3 bg-orange-100 rounded-lg">
                                        <span className="text-orange-600 text-xl">👥</span>
                                    </div>
                                </div>
                                <div className="mt-4">
                                    <span className="text-sm text-gray-600">
                                        Job posters
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Charts Grid */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Top Categories */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    Top Categories
                                </h3>
                                <div className="space-y-3">
                                    {result.top_categories?.slice(0, 8).map((category) => (
                                        <div key={category.category_id} className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                                <span className="text-sm font-medium text-gray-700">
                                                    {category.category_name}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {formatNumber(category.job_count)}
                                                </span>
                                                <span className="text-xs text-gray-500 ml-2">
                                                    ({category.percentage}%)
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Top Skills */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    Most Demanded Skills
                                </h3>
                                <div className="space-y-3">
                                    {result.top_skills?.slice(0, 8).map((skill) => (
                                        <div key={skill.skill_id} className="flex items-center justify-between">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                                <span className="text-sm font-medium text-gray-700">
                                                    {skill.skill_name}
                                                </span>
                                            </div>
                                            <div className="text-right">
                                                <span className="text-sm font-semibold text-gray-900">
                                                    {formatNumber(skill.usage_count)}
                                                </span>
                                                <span className="text-xs text-gray-500 ml-2">
                                                    ({skill.percentage}%)
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Job Types Distribution */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    Job Types
                                </h3>
                                <div className="space-y-3">
                                    {result.jobs_by_type?.map((type) => (
                                        <div key={type.type_name} className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700 capitalize">
                                                {type.type_name?.replace(/([A-Z])/g, ' $1').trim()}
                                            </span>
                                            <span className="text-sm font-semibold text-gray-900">
                                                {formatNumber(type.cant)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Popular Locations */}
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    Popular Locations
                                </h3>
                                <div className="space-y-3">
                                    {result.preferred_locations?.slice(0, 6).map((location) => (
                                        <div key={`${location.country_code}-${location.city}`} className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700">
                                                {location.city || 'Remote'} {location.country_code && `(${location.country_code})`}
                                            </span>
                                            <span className="text-sm font-semibold text-gray-900">
                                                {formatNumber(location.job_count)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "jobs" && (
                    <div className="space-y-6">
                        {/* Search and Filters */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                                    <div className="flex-1 w-full sm:max-w-md">
                                        <input
                                            type="text"
                                            placeholder="Search jobs by title, employer, or category..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        />
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) =>
                                                handleItemsPerPageChange(Number(e.target.value))
                                            }
                                            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        >
                                            <option value={20}>20 per page</option>
                                            <option value={50}>50 per page</option>
                                            <option value={100}>100 per page</option>
                                        </select>

                                        <div className="text-sm text-gray-600">
                                            Showing {filteredAndSortedJobs.length} of{" "}
                                            {result.paginate?.rows || 0} jobs
                                            <br />
                                            <span className="text-xs">
                                                Total: {formatNumber(result.total_jobs)} jobs
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Filter Row */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                                    <select
                                        value={filters.job_type}
                                        onChange={(e) => setFilters(prev => ({ ...prev, job_type: e.target.value }))}
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">All Job Types</option>
                                        <option value="1">Remote</option>
                                        <option value="2">In Person</option>
                                        <option value="3">Hybrid</option>
                                    </select>

                                    <select
                                        value={filters.job_status}
                                        onChange={(e) => setFilters(prev => ({ ...prev, job_status: e.target.value }))}
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="">All Statuses</option>
                                        <option value="1">Active</option>
                                        <option value="2">Completed</option>
                                        <option value="3">Expired</option>
                                    </select>

                                    <input
                                        type="date"
                                        value={filters.date_from}
                                        onChange={(e) => setFilters(prev => ({ ...prev, date_from: e.target.value }))}
                                        placeholder="From Date"
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />

                                    <input
                                        type="date"
                                        value={filters.date_to}
                                        onChange={(e) => setFilters(prev => ({ ...prev, date_to: e.target.value }))}
                                        placeholder="To Date"
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />

                                    <button
                                        onClick={() => setFilters({
                                            job_type: "",
                                            job_status: "",
                                            category: "",
                                            date_from: "",
                                            date_to: ""
                                        })}
                                        className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Jobs Table */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            {[
                                                { key: "job_id", label: "Job ID" },
                                                { key: "title", label: "Title" },
                                                { key: "category_name", label: "Category" },
                                                { key: "job_type", label: "Type" },
                                                { key: "budget", label: "Budget" },
                                                { key: "proposals", label: "Proposals" },
                                                { key: "date_created", label: "Posted" },
                                                { key: "job_status", label: "Status" },
                                            ].map(({ key, label }) => (
                                                <th
                                                    key={key}
                                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                    onClick={() => handleSort(key)}
                                                >
                                                    <div className="flex items-center space-x-1">
                                                        <span>{label}</span>
                                                        {sortConfig.key === key && (
                                                            <span>
                                                                {sortConfig.direction === "ascending"
                                                                    ? "↑"
                                                                    : "↓"}
                                                            </span>
                                                        )}
                                                    </div>
                                                </th>
                                            ))}
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredAndSortedJobs.map((job) => (
                                            <tr
                                                key={job.job_id}
                                                className="hover:bg-gray-50 cursor-pointer"
                                                onClick={() => setSelectedJob(job)}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    #{job.job_id}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div>
                                                        <div className="font-medium">{job.title}</div>
                                                        {job.employer_name && (
                                                            <div className="text-gray-500 text-xs">
                                                                by {job.employer_name}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {job.category_name}
                                                </td>
                                                {/* <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getJobTypeColor(job.job_type)}`}>
                                                        {job.job_type?.replace(/([A-Z])/g, ' $1').trim()}
                                                    </span>
                                                </td> */}
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {job.budget ? formatCurrency(job.budget, job.currency_code) : 'Not specified'}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <span className={`font-medium ${job.proposals > 0 ? 'text-green-600' : 'text-gray-600'}`}>
                                                        {job.proposals || 0}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    {formatDate(job.date_created)}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full capitalize ${getJobStatusColor(job.job_status)}`}>
                                                        {job.job_status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedJob(job);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-900"
                                                    >
                                                        View Details
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination Controls */}
                            {result.paginate && result.paginate.pages > 1 && (
                                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                    <div className="flex-1 flex justify-between sm:hidden">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage >= result.paginate.pages}
                                            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </div>
                                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                        <div>
                                            <p className="text-sm text-gray-700">
                                                Showing{" "}
                                                <span className="font-medium">
                                                    {(currentPage - 1) * itemsPerPage + 1}
                                                </span>{" "}
                                                to{" "}
                                                <span className="font-medium">
                                                    {Math.min(
                                                        currentPage * itemsPerPage,
                                                        result.paginate.rows
                                                    )}
                                                </span>{" "}
                                                of{" "}
                                                <span className="font-medium">
                                                    {result.paginate.rows}
                                                </span>{" "}
                                                results
                                            </p>
                                        </div>
                                        <div>
                                            <nav
                                                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                                aria-label="Pagination"
                                            >
                                                <button
                                                    onClick={() => handlePageChange(currentPage - 1)}
                                                    disabled={currentPage === 1}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <span className="sr-only">Previous</span>←
                                                </button>

                                                {/* Page numbers */}
                                                {pageNumbers.map((page) => (
                                                    <button
                                                        key={page}
                                                        onClick={() => handlePageChange(page)}
                                                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page
                                                            ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                                            : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                                            }`}
                                                    >
                                                        {page}
                                                    </button>
                                                ))}

                                                <button
                                                    onClick={() => handlePageChange(currentPage + 1)}
                                                    disabled={currentPage >= result.paginate.pages}
                                                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    <span className="sr-only">Next</span>→
                                                </button>
                                            </nav>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Empty State */}
                        {filteredAndSortedJobs.length === 0 && (
                            <div className="text-center py-12">
                                <div className="text-gray-400 text-6xl mb-4">🔍</div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                    No jobs found
                                </h3>
                                <p className="text-gray-600">
                                    {searchTerm || Object.values(filters).some(f => f)
                                        ? "Try adjusting your search criteria or filters"
                                        : "No jobs available"}
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Job Detail Modal */}
                {selectedJob && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-200">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">
                                            {selectedJob.title}
                                        </h2>
                                        <p className="text-gray-600">Job #{selectedJob.job_id}</p>
                                    </div>
                                    <button
                                        onClick={() => setSelectedJob(null)}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        <svg
                                            className="w-6 h-6"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <div className="p-6 space-y-6">
                                {/* Job Details */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            Job Information
                                        </h3>
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Employer:</span>
                                                <span className="font-medium">
                                                    {selectedJob.employer_name || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Category:</span>
                                                <span className="font-medium">
                                                    {selectedJob.category_name}
                                                </span>
                                            </div>
                                            {/* <div className="flex justify-between">
                                                <span className="text-gray-600">Job Type:</span>
                                                <span className={`px-2 py-1 text-xs rounded-full capitalize ${getJobTypeColor(selectedJob.job_type)}`}>
                                                    {selectedJob.job_type?.replace(/([A-Z])/g, ' $1').trim()}
                                                </span>
                                            </div> */}
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Status:</span>
                                                <span className={`px-2 py-1 text-xs rounded-full capitalize ${getJobStatusColor(selectedJob.job_status)}`}>
                                                    {selectedJob.job_status}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            Financial Information
                                        </h3>
                                        <div className="space-y-2">
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Budget:</span>
                                                <span className="font-medium text-green-600">
                                                    {selectedJob.budget ? formatCurrency(selectedJob.budget, selectedJob.currency_code) : 'Not specified'}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Proposals:</span>
                                                <span className="font-medium">
                                                    {selectedJob.proposals || 0}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Location:</span>
                                                <span className="font-medium">
                                                    {selectedJob.city || 'Remote'} {selectedJob.country_code && `(${selectedJob.country_code})`}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-600">Posted:</span>
                                                <span className="font-medium">
                                                    {formatDate(selectedJob.date_created)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Skills and Schedule */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {selectedJob.skills && selectedJob.skills.length > 0 && (
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                Required Skills
                                            </h3>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedJob.skills.map((skill, index) => (
                                                    <span
                                                        key={index}
                                                        className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                                                    >
                                                        {skill}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {selectedJob.time_slots && (
                                        <div className="space-y-4">
                                            <h3 className="text-lg font-semibold text-gray-900">
                                                Schedule
                                            </h3>
                                            <div className="text-sm text-gray-600">
                                                {selectedJob.time_slots.split(';').map((slot, index) => (
                                                    <div key={index}>{slot}</div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default JobStats;